import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Ams } from 'src/app/amsconfig';
import { IWorkgroup } from '../../settings/loss-code/types';
import {
  IAndonSequenceConfig,
  IAndonSequencePanel,
  IBundlingRule,
  IBundlingRuleTestResult,
  IMetricConfig,
  IExternalConnection,
  IDelayScrapCode,
  ICategory,
  IMachinePrintConfig,
  IBundlePrintConfig,
  ICoilPrintConfig,
  IDataSyncAlert,
  ILanguage,
  IUser,
  IEmployee,
  IExternalServicesReport,
  IRavenDbStatus,
  IProOnlineStatus,
  IProOnlineEnabledResult,
  IProOnlineDrainEstimate,
  IProOnlineHistoryResetResult,
  ColorRule,
  IClassicSystemSettings,
  IPackReindexStatus,
} from 'src/app/core/dto';
import { KeyDefinition, FileMapAndValidateResult } from '../../settings/integration/types';

interface IDefaultSequence {
  name: string;
  theme: string;
  panels: IAndonSequencePanel[];
}

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  private http = inject(HttpClient);
  
  private get baseUrl(): string {
    return Ams?.Config?.BASE_URL || '';
  }

  getWorkGroups() {
    return this.http.get<IWorkgroup[]>(`${this.baseUrl}/api/settings/workgroups`);
  }

  updateWorkGroup(workGroup: IWorkgroup) {
    return this.http.put<IWorkgroup>(`${this.baseUrl}/api/settings/workgroups`, workGroup);
  }

  addWorkGroup(workGroup: IWorkgroup) {
    return this.http.put<IWorkgroup>(
      `${this.baseUrl}/api/settings/workgroups/new`,
      workGroup
    );
  }

  deleteWorkGroup(id: string) {
    return this.http.delete(`${this.baseUrl}/api/settings/workgroups?id=${id}`);
  }

  updateMetric(configs: IMetricConfig[], machineNumber: number) {
    return this.http.put(
      `${this.baseUrl}/api/machine/${machineNumber}/metricsettings`,
      configs
    );
  }

  deletePanelSequence(id: string) {
    return this.http.delete(`${this.baseUrl}/api/andonSequences/${encodeURIComponent(id)}`);
  }

  addSequence(sequence: IDefaultSequence) {
    return this.http.post<IAndonSequenceConfig>(`${this.baseUrl}/api/andonSequences`, {
      sequence,
    });
  }

  updatePanelSequence(sequence: IAndonSequenceConfig) {
    return this.http.post(`${this.baseUrl}/api/andonSequences`, sequence);
  }

  updateBundlerRules(
    type: string,
    customer: string,
    tooling: string,
    material: string,
    rule: IBundlingRule
  ) {
    return this.http.post<any>(
      Ams.Config.BASE_URL +
        `/_api/integration/bundlerRules/${type}?customer=${customer}&tooling=${tooling}&material=${material}`,
      rule
    );
  }

  /// Resolve the bundler rules for a hypothetical selection without running a bundling pass.
  testBundlerRules(
    customer: string,
    tooling: string,
    material: string,
    orderMaxWeightLbs?: number,
    orderMaxPieceCount?: number
  ) {
    let params = new HttpParams()
      .set('customer', customer ?? '')
      .set('tooling', tooling ?? '')
      .set('material', material ?? '');
    if (orderMaxWeightLbs != null) {
      params = params.set('orderMaxWeightLbs', orderMaxWeightLbs);
    }
    if (orderMaxPieceCount != null) {
      params = params.set('orderMaxPieceCount', orderMaxPieceCount);
    }
    return this.http.get<IBundlingRuleTestResult>(
      Ams.Config.BASE_URL + '/_api/integration/bundlerRules/test',
      { params }
    );
  }

  deleteBundlerRule(type: string, customer: string, tooling: string, material: string) {
    return this.http.delete(
      Ams.Config.BASE_URL +
        `/_api/integration/bundlerRules/${type}?customer=${customer}&tooling=${tooling}&material=${material}`
    );
  }

  getExternalConnections() {
    return this.http.get<IExternalConnection[]>(
      `${this.baseUrl}/_api/integration/externalConnectionConfigs`
    );
  }

  getApiKeyDefinitions() {
    return this.http.get<KeyDefinition[]>(`${this.baseUrl}/_api/apikeys`);
  }

  deleteKey(id: string) {
    return this.http.delete(`${this.baseUrl}/_api/${id}`);
  }

  doTestFile(payload: FormData) {
    return this.http.post<FileMapAndValidateResult>(
      `${this.baseUrl}/_api/integration/testImportFile`,
      payload
    );
  }

  addApiKey(description: string) {
    return this.http.post<KeyDefinition>(`${this.baseUrl}/_api/apikeys/new`, {
      description,
      claims: ['api'],
    });
  }

  triggerLicenseUpdate() {
    return this.http.post<any>(`${this.baseUrl}/api/license/checknow`, {});
  }

  triggerSendInstallInfo() {
    return this.http.post<any>(`${this.baseUrl}/api/system/sendinstalldata`, {});
  }

  addLicense(licenseText: string) {
    return this.http.post(`${this.baseUrl}/api/license`, licenseText);
  }

  getDelayCodes() {
    return this.http.get<IDelayScrapCode[]>(`${this.baseUrl}/api/settings/delay`);
  }

  getDelayCategories() {
    return this.http.get<ICategory[]>(`${this.baseUrl}/api/settings/delaycategories`);
  }

  getDelayGroups() {
    return this.http.get(`${this.baseUrl}/api/settings/delay/groups`);
  }

  getDelayAlerts() {
    return this.http.get(`${this.baseUrl}/api/settings/delay/alerts`);
  }

  updateDelayCode(code: IDelayScrapCode) {
    return this.http.put(`${this.baseUrl}/api/settings/delay`, code);
  }

  deleteDelayCode(id: string) {
    return this.http.delete(`${this.baseUrl}/api/settings/delay?id=${id}`);
  }

  addDelayCode(code: IDelayScrapCode) {
    return this.http.put<IDelayScrapCode>(`${this.baseUrl}/api/settings/delay/new`, code);
  }

  updateDelayCodeWithRewrite(code: IDelayScrapCode, loggedProduction: number) {
    const url =
      loggedProduction > 0
        ? `${this.baseUrl}/api/settings/delay?rewriteHistory=true`
        : `${this.baseUrl}/api/settings/delay`;
    return this.http.put<IDelayScrapCode>(url, code);
  }

  getScrapCodes() {
    return this.http.get<IDelayScrapCode[]>(`${this.baseUrl}/api/settings/scrap`);
  }

  getScrapCategories() {
    return this.http.get<ICategory[]>(`${this.baseUrl}/api/settings/scrapcategories`);
  }

  getScrapGroups() {
    return this.http.get(`${this.baseUrl}/api/settings/scrap/groups`);
  }

  getScrapAlerts() {
    return this.http.get(`${this.baseUrl}/api/settings/scrap/alerts`);
  }

  updateScrapCode(code: IDelayScrapCode) {
    return this.http.put(`${this.baseUrl}/api/settings/scrap`, code);
  }

  deleteScrapCode(id: string) {
    return this.http.delete(`${this.baseUrl}/api/settings/scrap?id=${id}`);
  }

  addScrapCode(code: IDelayScrapCode) {
    return this.http.put<IDelayScrapCode>(`${this.baseUrl}/api/settings/scrap/new`, code);
  }

  updateScrapCodeWithRewrite(code: IDelayScrapCode, loggedProduction: number) {
    const url =
      loggedProduction > 0
        ? `${this.baseUrl}/api/settings/scrap?rewriteHistory=true`
        : `${this.baseUrl}/api/settings/scrap`;
    return this.http.put<IDelayScrapCode>(url, code);
  }

  saveBundleTagEnabled(bundleTagEnabled: boolean) {
    return this.http.post(`${this.baseUrl}/_api/printing/bundlePrintConfig`, {
      bundleTagEnabled,
    });
  }

  setBundleCompletionAlgorithm(title: string) {
    return this.http.post(`${this.baseUrl}/_api/printing/bundlePrintConfig`, {
      source: title,
    });
  }

  saveCoilTagEnabled(coilTagEnabled: boolean) {
    return this.http.post(`${this.baseUrl}/_api/printing/coilPrintConfig`, {
      coilTagEnabled,
    });
  }

  saveBundlePrintStartDate(startingDate: Date) {
    return this.http.post(`${this.baseUrl}/_api/printing/bundlePrintConfig`, {
      startingDate,
    });
  }

  saveCoilPrintStartDate(coilTagStartingDate: Date) {
    return this.http.post(`${this.baseUrl}/_api/printing/coilPrintConfig`, {
      coilTagStartingDate,
    });
  }

  saveCoilDefaultTemplate(defaultTemplate: string) {
    return this.http.post(`${this.baseUrl}/_api/printing/coilPrintConfig`, {
      defaultTemplate,
    });
  }

  updateMachineConfig(config: IMachinePrintConfig) {
    return this.http.post(
      `${this.baseUrl}/_api/printing/machinePrintConfigs?id=${config.id}`,
      config
    );
  }

  addTemplate(payload: FormData) {
    return this.http.put(`${this.baseUrl}/_api/printing/printTemplates`, payload);
  }

  getTempaltes() {
    return this.http.get(`${this.baseUrl}/_api/printing/printTemplates`);
  }

  printBundle(bundleCode: string) {
    return this.http.post<string>(`${this.baseUrl}/_api/bundle/${bundleCode}/print`, {});
  }

  triggerPrinterRefresh() {
    return this.http.post(`${this.baseUrl}/_api/printing/refreshPrinters`, {});
  }

  getBundlePrintConfig() {
    return this.http.get<IBundlePrintConfig>(
      `${this.baseUrl}/_api/printing/bundlePrintConfig`
    );
  }

  getCoilPrintConfig() {
    return this.http.get<ICoilPrintConfig>(`${this.baseUrl}/_api/printing/coilPrintConfig`);
  }

  updateBundlePrintConfig(props) {
    return this.http.post(`${this.baseUrl}/_api/printing/bundlePrintConfig`, props);
  }

  updateCoilPrintConfig(props) {
    return this.http.post(`${this.baseUrl}/_api/printing/coilPrintConfig`, props);
  }

  getDataSyncAlert() {
    return this.http.get<IDataSyncAlert>(`${this.baseUrl}/api/system/dataSyncAlert`);
  }

  getExternalServicesStatus(forceRefresh: boolean = false) {
    const params = forceRefresh ? '?forceRefresh=true' : '';
    return this.http.get<IExternalServicesReport>(
      `${this.baseUrl}/api/system/externalServices${params}`
    );
  }

  getRavenDbStatus() {
    return this.http.get<IRavenDbStatus>(`${this.baseUrl}/api/system/ravendb`);
  }

  // ── Pro Online export channel ────────────────────────────────────

  getProOnlineStatus() {
    return this.http.get<IProOnlineStatus>(`${this.baseUrl}/_api/pro-online/status`);
  }

  setProOnlineEnabled(enabled: boolean) {
    return this.http.post<IProOnlineEnabledResult>(`${this.baseUrl}/_api/pro-online/enabled`, {
      enabled,
    });
  }

  getProOnlineDrainEstimate() {
    return this.http.get<IProOnlineDrainEstimate>(`${this.baseUrl}/_api/pro-online/drain-estimate`);
  }

  requestProOnlineHistoryReset() {
    return this.http.post<IProOnlineHistoryResetResult>(
      `${this.baseUrl}/_api/pro-online/history-reset`,
      {},
    );
  }

  triggerHistorySync() {
    return this.http.post(`${this.baseUrl}/api/system/startHistorySync`, {});
  }

  doDiagnosticUpload() {
    return this.http.post(`${this.baseUrl}/api/system/diagnostics/send`, {});
  }
  synchronizeOrdersWithAgent() {
    return this.http.post<string>(`${this.baseUrl}/api/orders/synccleanup`, {});
  }

  triggerSync(doc: string) {
    return this.http.post(`${this.baseUrl}/api/system/forceAgentSync/${doc.slice(10)}`, {});
  }

  getLanguages() {
    return this.http.get<ILanguage[]>(`${this.baseUrl}/api/languages`);
  }

  updatePreferences(pref: { [key: string]: string | boolean }) {
    return this.http.post(`${this.baseUrl}/api/systemPreferences`, pref);
  }

  /**
   * Save the system-level color rules. Uses a dedicated endpoint (rather than
   * the generic preferences patch) because the patch path only handles scalars
   * and string arrays, not a list of objects. See `specs/color-rules.md`.
   */
  updateColorRules(rules: ColorRule[]) {
    return this.http.post(`${this.baseUrl}/api/systemPreferences/colorRules`, rules);
  }

  /**
   * Save the Eclipse Classic system settings (table cleanup / controllers / timing). Uses a
   * dedicated endpoint (rather than the generic preferences patch) because the payload is a nested
   * object; the server also writes these back to Classic's xlwin.ini through the agent.
   */
  updateClassicSettings(settings: IClassicSystemSettings) {
    return this.http.post(`${this.baseUrl}/api/systemPreferences/classicSettings`, settings);
  }

  /** Trigger an immediate Eclipse Classic pack & reindex (companion to the scheduled pack). */
  triggerPackReindexNow() {
    return this.http.post(`${this.baseUrl}/api/systemPreferences/packReindexNow`, {});
  }

  /** Poll whether a manual pack & reindex is currently running (drives the button spinner). */
  getPackReindexStatus() {
    return this.http.get<IPackReindexStatus>(
      `${this.baseUrl}/api/systemPreferences/packReindexStatus`
    );
  }

  removeRole(userName: string, role: string) {
    return this.http.post<IUser>(
      `${this.baseUrl}/_api/users/${userName}/removerole?role=${role}`,
      {
        role,
      }
    );
  }

  addRole(userName: string, role: string) {
    return this.http.post<IUser>(
      `${this.baseUrl}/_api/users/${userName}/addrole?role=${role}`,
      {
        role,
      }
    );
  }

  setPassword(username: string, password: string, passwordConfirm: string) {
    return this.http.post<any>(`${this.baseUrl}/_api/users/${username}/setpassword`, {
      username,
      password,
      passwordConfirm,
    });
  }

  saveUserField(userName: string, field: string, value: string) {
    return this.http.patch(Ams.Config.BASE_URL + `/_api/users/${userName}?${field}=${value}`, {});
  }

  /**
   * Save language and/or units overrides for the *currently authenticated* user.
   * Pass an empty string to clear an override (fall back to system default).
   * A null/undefined value means "no change".
   */
  saveSelfPreferences(prefs: { language?: string; inchesUnit?: string }) {
    const body: { language?: string; inchesUnit?: string } = {};
    if (prefs.language !== undefined && prefs.language !== null) {
      body.language = prefs.language;
    }
    if (prefs.inchesUnit !== undefined && prefs.inchesUnit !== null) {
      body.inchesUnit = prefs.inchesUnit;
    }
    return this.http.post(`${this.baseUrl}/_api/user/settings`, body);
  }

  /**
   * Admin path: save language and/or units overrides for *another* user.
   * Pass an empty string to clear an override (fall back to system default).
   */
  saveUserPreferences(userName: string, prefs: { language?: string; inchesUnit?: string }) {
    const params = new URLSearchParams();
    if (prefs.language !== undefined && prefs.language !== null) {
      params.set('language', prefs.language);
    }
    if (prefs.inchesUnit !== undefined && prefs.inchesUnit !== null) {
      params.set('inchesUnit', prefs.inchesUnit);
    }
    return this.http.patch<IUser>(
      `${this.baseUrl}/_api/users/${userName}?${params.toString()}`,
      {}
    );
  }

  /**
   * Create a new user. A random password will be generated and returned.
   */
  createUser(params: CreateUserParams) {
    return this.http.post<CreateUserResult>(`${this.baseUrl}/_api/users/create`, params);
  }

  /**
   * Generate a one-time password for an existing user.
   * Sets RequirePasswordChange=true so user must change it on first login.
   */
  generatePassword(username: string) {
    return this.http.post<GeneratePasswordResult>(
      `${this.baseUrl}/_api/users/${username}/generatepassword`,
      {}
    );
  }

  /**
   * Delete a user. The server refuses the guest, the logged-in user, and the last administrator.
   */
  deleteUser(userName: string) {
    return this.http.delete(`${this.baseUrl}/_api/users/${encodeURIComponent(userName)}`);
  }

  // ── Classic Employees (XL operators) ─────────────────────────────

  listEmployees() {
    return this.http.get<IEmployee[]>(`${this.baseUrl}/_api/employees`);
  }

  promoteEmployee(employId: number, params: PromoteEmployeeParams) {
    return this.http.post<PromoteEmployeeResult>(
      `${this.baseUrl}/_api/employees/${employId}/promote`,
      params
    );
  }

  assignEmployeeId(userName: string, employId: number) {
    return this.http.post<AssignEmployeeIdResult>(
      `${this.baseUrl}/_api/employees/assign`,
      { userName, employId }
    );
  }

  // ── Shift Times ──────────────────────────────────────────────────

  getSystemShiftDefaults() {
    return this.http.get<ISystemShiftDefaults>(
      `${this.baseUrl}/api/settings/shifttimes/system`
    );
  }

  updateSystemShiftDefaults(defaults: ISystemShiftDefaults, force = false) {
    const q = force ? '?force=true' : '';
    return this.http.put(`${this.baseUrl}/api/settings/shifttimes/system${q}`, defaults);
  }

  getMachineShiftDefaults(machineNumber: number) {
    return this.http.get<IMachineShiftDefaults>(
      `${this.baseUrl}/api/settings/shifttimes/machine/${machineNumber}`
    );
  }

  updateMachineShiftDefaults(machineNumber: number, defaults: IMachineShiftDefaults, force = false) {
    const q = force ? '?force=true' : '';
    return this.http.put(
      `${this.baseUrl}/api/settings/shifttimes/machine/${machineNumber}${q}`,
      defaults
    );
  }

  setMachineUseSystemDefaults(machineNumber: number, useDefaults: boolean) {
    return this.http.put(
      `${this.baseUrl}/api/settings/shifttimes/machine/${machineNumber}/usesystemdefaults`,
      { useDefaults }
    );
  }

  getShiftTimeExceptions(machineNumber: number) {
    return this.http.get<IShiftTimeException[]>(
      `${this.baseUrl}/api/settings/shifttimes/exceptions/${machineNumber}`
    );
  }

  upsertShiftTimeException(exception: IShiftTimeException, force = false) {
    const q = force ? '?force=true' : '';
    return this.http.put<IShiftTimeException>(
      `${this.baseUrl}/api/settings/shifttimes/exceptions${q}`,
      exception
    );
  }

  deleteShiftTimeException(shiftId: number, force = false) {
    const q = force ? '?force=true' : '';
    return this.http.delete(
      `${this.baseUrl}/api/settings/shifttimes/exceptions/${shiftId}${q}`
    );
  }

  getResolvedShiftSchedule(machineNumber: number, date: string) {
    return this.http.get<IResolvedShiftSchedule>(
      `${this.baseUrl}/api/settings/shifttimes/resolved/${machineNumber}?date=${date}`
    );
  }
}

export interface CreateUserParams {
  userName: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  pin?: string;
  roles?: string[];
  requirePasswordChange?: boolean;
  employId?: number;
}

export interface CreateUserResult {
  user: IUser;
  generatedPassword?: string;
  warnings?: string[];
}

export interface GeneratePasswordResult {
  generatedPassword: string;
  message: string;
}

export interface PromoteEmployeeParams {
  mode: 'link' | 'create';
  userName: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

export interface PromoteEmployeeResult {
  employee: { employId: number; name: string; changeId: number };
  user: IUser;
  generatedPassword?: string;
}

export interface AssignEmployeeIdResult {
  employee: { employId: number; name: string; changeId: number };
  user: IUser;
}

// ── Shift Times ────────────────────────────────────────────────────

export interface IShiftDefinition {
  start1: string;
  end1: string;
  start2: string;
  end2: string;
  start3: string;
  end3: string;
}

export interface ISystemShiftDefaults extends IShiftDefinition {}

export interface IMachineShiftDefaults extends IShiftDefinition {
  machineNumber: number;
  useSystemDefaults: boolean;
}

export interface IShiftTimeException extends IShiftDefinition {
  shiftId: number;
  machineId: number;
  machineNumber: number;
  type: string;
  date?: string;
  effectiveDate?: string;
  useDefault: boolean;
}

export interface IResolvedShiftSchedule extends IShiftDefinition {
  machineNumber: number;
  date: string;
  source: string;
}
